import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { ErrorState, LoadingState, PageHeader } from '../../../group_leader/components/common'
import { makeT } from '../../../group_leader/helpers/i18n'
import {
  glApi,
  useListRoundsQuery,
  useListTournamentSpecsQuery,
  useListUserTournamentSpecsQuery,
  useListLeagueCoursesQuery,
  useGetTeeSheetQuery,
  useGetUnplacedPlayersQuery,
  useToggleTournamentStatusMutation,
  useCreateRowMutation,
  useSetCourseAndTeeMutation,
  useSetHoleMutation,
} from '../../../group_leader/store/glApi'
import { fieldCourseId, fieldTeeId, teeWritesForField } from '../../helpers/pairings'
import { placeWalkonField, teesFromSheet } from '../../helpers/walkonPlacement'
import { showErrorNotification } from '../../../shared/helpers'
import WizardFooter from '../common/WizardFooter'
import { goToStep, goToPreviousStep, markStepCompleted } from '../../store/wizardSlice'
import {
  displayTime12,
  groupOccupantNames,
  groupSizeKey,
  holesPlayedKey,
  lostItsData,
  resolveCourseAndTee,
  resolveSchedule,
  specDetailLine,
  specDisplayName,
  summarizeRoster,
  teeCounts,
} from '../../helpers/review'

const t = makeT('group_leader.event_setup.review')
// The rows are the steps, so they take the header names rather than a second copy.
const stepT = makeT('group_leader.event_setup.header.steps')
// Holes played is the Course & Tee step's own wording, read from its namespace.
const courseTeeT = makeT('group_leader.event_setup.course_tee')

const GROUPS_BLOCK_ID = 'gl-review-groups'

// The noun inflects with the count, so i18n picks the plural rather than a fixed suffix.
const groupSizeLabel = (size, count) => {
  const key = groupSizeKey(size)
  return key ? t(key, { count }) : t('group_size_of', { n: size, count })
}

// One block of the summary card; its header owns its own Edit control.
const SummaryRow = ({ label, onEdit, children }) => (
  <div className="gl-event-setup-summary-row">
    <div className="gl-event-setup-summary-body">
      <div className="gl-event-setup-summary-head">
        <div className="gl-event-setup-summary-label">{label}</div>
        <button
          className="gl-event-setup-summary-edit"
          type="button"
          onClick={onEdit}
          aria-label={t('edit_aria', { section: label })}
          >
          {t('edit')}
        </button>
      </div>
      {children}
    </div>
  </div>
)

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  children: PropTypes.node,
}

// What a step shows when it has nothing to state, or nothing it can see from here.
const MissingValue = ({ children }) => (
  <div className="gl-event-setup-summary-value-muted">{children}</div>
)

MissingValue.propTypes = { children: PropTypes.node }

const GamesSummary = ({ specs }) => {
  if (specs.length === 0) {
    return <MissingValue>{t('no_game')}</MissingValue>
  }

  return specs.map((spec) => {
    const detail = specDetailLine(spec)

    return (
      <div key={spec.id} className="gl-event-setup-game-entry">
        <div className="gl-event-setup-summary-value">{specDisplayName(spec.name || spec.id)}</div>
        {detail && <div className="gl-event-setup-summary-detail">{detail}</div>}
      </div>
    )
  })
}

GamesSummary.propTypes = {
  specs: PropTypes.arrayOf(PropTypes.object).isRequired,
}

const SchedulePills = ({ schedule }) => (
  <div className="gl-event-setup-schedule-pills">
    <span className="gl-event-setup-s-pill">{schedule.isShotgun ? t('shotgun') : t('sequential')}</span>
    {schedule.time && <span className="gl-event-setup-s-pill">{schedule.time}</span>}
    {schedule.interval && (
      <span className="gl-event-setup-s-pill">{t('interval_pill', { interval: schedule.interval })}</span>
    )}
    {schedule.startHole && (
      <span className="gl-event-setup-s-pill">{t('hole_chip', { n: schedule.startHole })}</span>
    )}
  </div>
)

SchedulePills.propTypes = {
  schedule: PropTypes.object.isRequired,
}

const PairingsSummary = ({
  isWalkon, groupCount, groupSize, schedule, showGroups, onToggleGroups,
}) => {
  // A walk-on event has no tee sheet, so an empty one is configured, not missing.
  if (isWalkon) {
    return <div className="gl-event-setup-summary-value">{t('walkon_summary')}</div>
  }
  if (groupCount === 0) {
    return <MissingValue>{t('no_pairings')}</MissingValue>
  }

  return (
    <div>
      <div className="gl-event-setup-summary-value">
        {groupCount} {groupSizeLabel(groupSize, groupCount)}
      </div>
      {schedule.isSet && <SchedulePills schedule={schedule}/>}
      <button
        className="gl-event-setup-expand-btn"
        type="button"
        onClick={onToggleGroups}
        aria-expanded={showGroups}
        aria-controls={GROUPS_BLOCK_ID}
        >
        {showGroups ? t('hide_groups') : t('show_groups')}
      </button>
    </div>
  )
}

PairingsSummary.propTypes = {
  // Walk-on / no tee sheet: the step renders the mode rather than the (necessarily empty) sheet.
  isWalkon: PropTypes.bool,
  groupCount: PropTypes.number.isRequired,
  groupSize: PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]).isRequired,
  schedule: PropTypes.object.isRequired,
  showGroups: PropTypes.bool.isRequired,
  onToggleGroups: PropTypes.func.isRequired,
}

// The tee sheet, group by group, revealed under the pairings row.
const GroupList = ({ groups, hidden }) => (
  <div className="gl-event-setup-groups-block" id={GROUPS_BLOCK_ID} hidden={hidden}>
    {groups.map((group, index) => {
      const time = displayTime12(group.attributes?.tee_time)
      const hole = group.attributes?.hole || group.attributes?.tee_number

      return (
        <div key={group.id} className="gl-event-setup-group-row">
          <div className="gl-event-setup-group-meta">
            <span className="gl-event-setup-group-num">{t('group_label', { n: index + 1 })}</span>
            {hole && <span className="gl-event-setup-group-chip">{t('hole_chip', { n: hole })}</span>}
            {time && <span className="gl-event-setup-group-chip">{time}</span>}
          </div>
          <div className="gl-event-setup-group-names">
            {groupOccupantNames(group).join(' · ')}
          </div>
        </div>
      )
    })}
  </div>
)

GroupList.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  hidden: PropTypes.bool,
}

const CourseTeeSummary = ({ courseName, teeName, tees, known, holeType }) => {
  // Not "unset": the event has a course, this step just cannot always see it.
  if (!known) {
    return <MissingValue>{t('course_tee_unknown')}</MissingValue>
  }

  const holesKey = holesPlayedKey(holeType)

  return (
    <>
      <div className="gl-event-setup-summary-value">{courseName}</div>
      {holesKey && <div className="gl-event-setup-summary-detail">{courseTeeT(holesKey)}</div>}
      {tees.length > 1
        ? tees.map((tee) => (
          <div key={tee.name} className="gl-event-setup-summary-detail">
            {t('tee_golfer_count', { tee: tee.name, count: tee.count })}
          </div>
        ))
        : teeName && <div className="gl-event-setup-summary-detail">{teeName}</div>
      }
    </>
  )
}

CourseTeeSummary.propTypes = {
  // Whether the course could be determined at all, as opposed to being absent.
  known: PropTypes.bool,
  courseName: PropTypes.string,
  teeName: PropTypes.string,
  // '1' all 18, '2' front 9, '3' back 9, 'nine' a nine-hole course
  holeType: PropTypes.string,
  tees: PropTypes.arrayOf(PropTypes.object).isRequired,
}

// Last step: the four steps' config read back from the API, plus the action that starts it.
const ReviewStep = () => {
  const dispatch = useDispatch()
  const leagueId = useSelector((state) => state.misc.data.leagueId)
  const csrfToken = useSelector((state) => state.misc.data.csrfToken)
  const leagueDashboardUrl = useSelector((state) => state.misc.data.leagueDashboardUrl)
  const leagueResultsUrl = useSelector((state) => state.misc.data.leagueResultsUrl)
  const roundStatusUrl = useSelector((state) => state.misc.data.roundStatusUrl)
  const eventName = useSelector((state) => state.misc.data.eventName)
  const roundId = useSelector((state) => state.wizard.roundId)
  const setupConfig = useSelector((state) => state.wizard.setupConfig)

  const [ starting, setStarting ] = useState(false)
  const [ showGroups, setShowGroups ] = useState(false)

  const { data: roundsData, isLoading: roundsLoading, error: roundsError } =
    useListRoundsQuery(leagueId, { skip: !leagueId })

  const { data: specsData, isLoading: specsLoading, error: specsError } =
    useListTournamentSpecsQuery({ leagueId, roundId }, { skip: !leagueId || !roundId })

  const { data: userSpecsData, isLoading: userSpecsLoading, error: userSpecsError } =
    useListUserTournamentSpecsQuery({ leagueId, roundId }, { skip: !leagueId || !roundId })

  const { data: teeSheetData, isLoading: teeSheetLoading, error: teeSheetError, refetch: refetchTeeSheet } =
    useGetTeeSheetQuery({ leagueId, roundId }, { skip: !leagueId || !roundId })

  const { data: unplacedData, error: unplacedError } =
    useGetUnplacedPlayersQuery({ leagueId, roundId }, { skip: !leagueId || !roundId })

  const { data: coursesData, isLoading: coursesLoading, error: coursesError } =
    useListLeagueCoursesQuery(leagueId, { skip: !leagueId })

  const [ toggleTournamentStatus ] = useToggleTournamentStatusMutation()
  const [ createRow ] = useCreateRowMutation()
  const [ setCourseAndTee ] = useSetCourseAndTeeMutation()
  const [ setHole ] = useSetHoleMutation()

  const round = roundsData?.data?.find((entry) => String(entry.id) === String(roundId))
    || roundsData?.data?.[0]
  // The event name is the heading; "Round 1" says nothing on a one-round event.
  const heading = eventName || round?.attributes?.name || t('default_round_name')
  const groupSize = round?.attributes?.pairing_group_size || 4
  // Walk-on is group size 1 server-side, the signal the other steps use.
  const isWalkon = setupConfig?.startType === 'walkon'
    || String(round?.attributes?.pairing_group_size) === '1'
  // Already live: starting again would re-notify GGS and re-invalidate the leaderboard.
  const alreadyStarted = !!round?.attributes?.status && round.attributes.status !== 'not started'
  // Hand-scored add-ons are their own endpoint; without them the games count reads short.
  const specs = [ ...(specsData?.data || []), ...(userSpecsData?.data || []) ]
  const foursomes = teeSheetData?.data || []
  const unplacedPlayers = unplacedData?.players || []

  const roster = summarizeRoster(foursomes, unplacedPlayers)
  // Placed golfers only: an unplaced golfer has no tee to be counted on.
  const tees = teeCounts(roster.placedPlayers)
  const { courseName, teeName, known: courseKnown, holeType } = resolveCourseAndTee({
    setupConfig,
    courses: coursesData?.data || [],
    placedPlayers: roster.placedPlayers,
    tees,
  })
  const schedule = resolveSchedule(setupConfig, foursomes)

  // Completion is a property of the event's data, not of which steps were mounted.
  useEffect(() => {
    if (specs.length > 0) { dispatch(markStepCompleted('games')) }
    if (roster.totalEnrolled > 0) { dispatch(markStepCompleted('players')) }
    // A walk-on event has no tee sheet by definition, so an enrolled roster is all "paired" means.
    if (isWalkon ? roster.totalEnrolled > 0 : roster.groupCount > 0) {
      dispatch(markStepCompleted('pairings'))
    }
    // A golfer's tee is the durable evidence; walk-on tees its own rows (placeWalkonField).
    if (tees.length > 0 || (isWalkon && setupConfig?.walkonTeeM)) {
      dispatch(markStepCompleted('course_tee'))
    }
  }, [ specs.length, roster.totalEnrolled, roster.groupCount, tees.length, isWalkon ])

  // Starts the round, then hands over to the results page, substituting ROUND_PLACEHOLDER.
  const goToResults = () => {
    window.location.href = leagueResultsUrl
      ? leagueResultsUrl.replace('ROUND_PLACEHOLDER', roundId)
      : (leagueDashboardUrl || '/')
  }

  // Course & Tee places the walk-on field; nothing places whoever enrolls after it, leaving them
  // with no group, pin or tee. Tees come off the sheet, so a latecomer joins what others play.
  const placeLateArrivals = async () => {
    if (!isWalkon || unplacedPlayers.length === 0 || foursomes.length === 0) { return true }

    try {
      return await placeWalkonField({
        round: { leagueId, roundId },
        field: { unplaced: unplacedPlayers, foursomes },
        tees: teesFromSheet(foursomes),
        api: { createRow, setCourseAndTee, setHole, refetchTeeSheet },
        onError: () => showErrorNotification(t('errors.start_failed')),
      })
    } finally {
      dispatch(glApi.util.invalidateTags([ 'TeeSheet' ]))
    }
  }

  // A leader can skip Course & Tee and Pairings from the step bar, so tee the field here too.
  const assignTeesToField = async () => {
    const players = foursomes.flatMap((group) => group.attributes?.players || [])
    if (!roundId || players.length === 0) { return true }

    const courseId = fieldCourseId(players, setupConfig?.courseId)
    const fallbackTeeId = fieldTeeId(players, { courseId, configTeeId: setupConfig?.teeId })

    for (const [ teeId, playerIds ] of teeWritesForField(players, { courseId, fallbackTeeId })) {
      // eslint-disable-next-line no-await-in-loop
      const result = await setCourseAndTee({ leagueId, roundId, teeId, playerIds })
      if (result.error) { showErrorNotification(t('errors.start_failed')); return false }
    }
    return true
  }

  const handleStart = async () => {
    // Already live: hand over to the results page rather than start it a second time.
    if (!alreadyStarted && (!roundStatusUrl || !roundId)) {
      showErrorNotification(t('errors.start_failed'))
      return
    }

    setStarting(true)
    try {
      if (!(await placeLateArrivals()) || !(await assignTeesToField())) { setStarting(false); return }

      if (!alreadyStarted) {
        const result = await toggleTournamentStatus({
          statusUrl: roundStatusUrl.replace('ROUND_PLACEHOLDER', roundId),
          leagueId,
          status: 'in progress',
          csrfToken,
        })
        if (result.error) {
          showErrorNotification(t('errors.start_failed'))
          setStarting(false)
          return
        }
      }
    } catch (_error) {
      showErrorNotification(t('errors.unexpected'))
      setStarting(false)
      return
    }
    goToResults()
  }

  if (roundsLoading || specsLoading || userSpecsLoading || teeSheetLoading || coursesLoading) {
    return <LoadingState label={t('loading')}/>
  }

  // A failed read leaves the lists empty, which would read as a configured but empty event.
  if (lostItsData([
    { error: roundsError, data: roundsData },
    { error: specsError, data: specsData },
    { error: userSpecsError, data: userSpecsData },
    { error: teeSheetError, data: teeSheetData },
    { error: unplacedError, data: unplacedData },
    { error: coursesError, data: coursesData },
  ])) {
    return <ErrorState/>
  }

  return (
    <div className="gl-event-setup-wrap">
      <PageHeader title={heading} subtitle={t('subtitle')}/>

      <div className="gl-event-setup-summary-card">
        <SummaryRow
          label={t('games_label', { count: specs.length })}
          onEdit={() => dispatch(goToStep('games'))}
          >
          <GamesSummary specs={specs}/>
        </SummaryRow>

        <SummaryRow label={stepT('players')} onEdit={() => dispatch(goToStep('players'))}>
          {roster.totalEnrolled > 0
            ? <div className="gl-event-setup-summary-value">{t('players_count', { count: roster.totalEnrolled })}</div>
            : <MissingValue>{t('no_golfers')}</MissingValue>
          }
        </SummaryRow>

        <SummaryRow label={stepT('pairings')} onEdit={() => dispatch(goToStep('pairings'))}>
          <PairingsSummary
            isWalkon={isWalkon}
            groupCount={roster.groupCount}
            groupSize={groupSize}
            schedule={schedule}
            showGroups={showGroups}
            onToggleGroups={() => setShowGroups((shown) => !shown)}
            />
        </SummaryRow>

        {!isWalkon && roster.groupCount > 0 && (
          <GroupList groups={roster.occupiedGroups} hidden={!showGroups}/>
        )}

        <SummaryRow label={stepT('course_tee')} onEdit={() => dispatch(goToStep('course_tee'))}>
          <CourseTeeSummary
            courseName={courseName}
            teeName={teeName}
            tees={tees}
            known={courseKnown}
            holeType={holeType}
            />
        </SummaryRow>
      </div>

      <WizardFooter
        onBack={() => dispatch(goToPreviousStep())}
        onPrimary={handleStart}
        primaryLabel={t('finish_setup')}
        primaryLoading={starting}
        primaryWide
        />
    </div>
  )
}

export default ReviewStep
